import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ChevronUp, ChevronDown, Trash2, Plus, Eye } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  rating: "Score out of 5",
  choice: "Pick one answer",
  text: "Write an answer",
};

export const SurveyQuestionsDialog = ({ onClose }) => {
  const [questions, setQuestions] = useState([]);
  const [original, setOriginal] = useState("");
  const [ageGroups, setAgeGroups] = useState([]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    axios.get(`${API}/academy/trials/survey-questions`, { withCredentials: true })
      .then((r) => {
        const loaded = (r.data.questions || []).map((q) => ({ ...q, options: q.options || [], age_groups: q.age_groups || [] }));
        setQuestions(loaded);
        setOriginal(JSON.stringify(loaded));
        setAgeGroups(r.data.age_groups || []);
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load the questions"));
  }, []);

  const closeGuarded = () => {
    const dirty = original && JSON.stringify(questions.map(({ options_text, ...q }) => q)) !== original;
    if (dirty && !window.confirm("Close without saving? Your changes to the questions will be lost.")) return;
    onClose();
  };

  const patch = (i, changes) => setQuestions((qs) => qs.map((q, n) => (n === i ? { ...q, ...changes } : q)));
  const move = (i, by) => setQuestions((qs) => {
    const next = [...qs];
    const j = i + by;
    if (j < 0 || j >= next.length) return qs;
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const remove = (i) => setQuestions((qs) => qs.filter((_, n) => n !== i));
  const add = () => setQuestions((qs) => [...qs, {
    id: "", type: "rating", label: "", options: [], age_groups: [], active: true, required: false,
  }]);

  const toggleGroup = (i, group) => {
    const current = questions[i].age_groups || [];
    patch(i, { age_groups: current.includes(group) ? current.filter((g) => g !== group) : [...current, group] });
  };

  const save = async () => {
    setBusy(true);
    // Options are kept as raw text while typing so spaces survive; split here.
    const payload = questions.map(({ options_text, ...q }) => ({
      ...q,
      options: options_text !== undefined
        ? options_text.split(",").map((o) => o.trim()).filter(Boolean)
        : (q.options || []),
    }));
    try {
      const r = await axios.put(`${API}/academy/trials/survey-questions`,
        { questions: payload }, { withCredentials: true });
      setQuestions(r.data.questions);
      toast.success("Saved — parents will see this from the next survey");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the questions");
    } finally { setBusy(false); }
  };

  const showPreview = async (group) => {
    try {
      const r = await axios.get(`${API}/academy/trials/survey-questions/preview`,
        { params: { age_group: group }, withCredentials: true });
      setPreview({ group: group || "every age group", questions: r.data.questions });
    } catch (e) {
      toast.error("Could not build the preview");
    }
  };

  return (
    <Dialog open onOpenChange={closeGuarded}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto"        data-testid="survey-questions-dialog">
        <DialogHeader><DialogTitle>Survey questions</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-400">
          What the parent is asked after the trial. Leave the age groups untouched and everyone gets
          the question; pick one or more and only those parents see it. Answers already collected keep
          their wording on the results page.
        </p>

        <div className="space-y-3">
          {questions.map((q, i) => (
            <div key={`${q.id || "new"}-${i}`}
              className={`rounded-lg border p-3 ${q.active === false ? "border-slate-800 bg-slate-950/60 opacity-70" : "border-slate-700 bg-slate-950/40"}`}
              data-testid={`question-card-${i}`}>
              <div className="flex items-start gap-2">
                <div className="flex flex-col gap-1 pt-1">
                  <button onClick={() => move(i, -1)} className="text-slate-500 hover:text-white"
                    data-testid={`question-up-${i}`} aria-label="Move up"><ChevronUp className="w-4 h-4" /></button>
                  <button onClick={() => move(i, 1)} className="text-slate-500 hover:text-white"
                    data-testid={`question-down-${i}`} aria-label="Move down"><ChevronDown className="w-4 h-4" /></button>
                </div>
                <div className="flex-1 min-w-0 space-y-2">
                  <Input value={q.label} onChange={(e) => patch(i, { label: e.target.value })}
                    placeholder="e.g. Was the session the right level for your child?"
                    className="bg-slate-900 border-slate-700 text-sm" data-testid={`question-label-${i}`} />
                  <div className="flex flex-wrap items-center gap-2">
                    <select value={q.type}
                      onChange={(e) => patch(i, { type: e.target.value })}
                      className="bg-slate-900 border border-slate-700 rounded-md text-xs px-2 py-1.5 text-slate-200"
                      data-testid={`question-type-${i}`}>
                      {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <button
                      className={`h-7 px-2.5 rounded-full border text-[11px] ${q.active === false ? "border-amber-500/60 bg-amber-500/10 text-amber-300" : "border-emerald-500/50 bg-emerald-500/10 text-emerald-300"}`}
                      onClick={() => patch(i, { active: q.active === false })}
                      data-testid={`question-active-${i}`}>
                      {q.active === false ? "Switched off" : "Asked"}
                    </button>
                    <button
                      className={`h-7 px-2.5 rounded-full border text-[11px] ${q.required ? "border-cyan-500/60 bg-cyan-500/10 text-cyan-200" : "border-slate-700 text-slate-400"}`}
                      onClick={() => patch(i, { required: !q.required })}
                      data-testid={`question-required-${i}`}>
                      {q.required ? "Must answer" : "Optional"}
                    </button>
                    <button onClick={() => remove(i)} className="text-red-400/80 hover:text-red-300 ml-auto"
                      data-testid={`question-remove-${i}`} aria-label="Remove"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  {q.type === "choice" && (
                    <Input value={q.options_text !== undefined ? q.options_text : (q.options || []).join(", ")}
                      onChange={(e) => patch(i, { options_text: e.target.value })}
                      placeholder="Answers to choose from, separated by commas — Yes, Somewhat, No"
                      className="bg-slate-900 border-slate-700 text-xs" data-testid={`question-options-${i}`} />
                  )}
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[11px] text-slate-500 mr-1">Ask this of:</span>
                    <button onClick={() => patch(i, { age_groups: [] })}
                      className={`text-[11px] px-2 py-0.5 rounded-full border ${(q.age_groups || []).length === 0 ? "border-cyan-500 bg-cyan-500/20 text-cyan-200" : "border-slate-700 text-slate-400"}`}
                      data-testid={`question-ages-all-${i}`}>All age groups</button>
                    {ageGroups.map((g) => (
                      <button key={g} onClick={() => toggleGroup(i, g)}
                        className={`text-[11px] px-2 py-0.5 rounded-full border ${(q.age_groups || []).includes(g) ? "border-cyan-500 bg-cyan-500/20 text-cyan-200" : "border-slate-700 text-slate-400"}`}
                        data-testid={`question-age-${i}-${g}`}>{g}</button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" className="h-8 text-[11px] border-slate-600"
            onClick={add} data-testid="question-add"><Plus className="w-3.5 h-3.5 mr-1" />Add a question</Button>
          <Button size="sm" variant="ghost" className="h-8 text-[11px] text-slate-300"
            onClick={() => showPreview("")} data-testid="question-preview-all">
            <Eye className="w-3.5 h-3.5 mr-1" />Preview
          </Button>
          {ageGroups.map((g) => (
            <Button key={g} size="sm" variant="ghost" className="h-8 text-[11px] text-slate-400"
              onClick={() => showPreview(g)} data-testid={`question-preview-${g}`}>{g}</Button>
          ))}
        </div>

        {preview && (
          <div className="rounded-lg border border-slate-700 bg-slate-950/60 p-3" data-testid="question-preview-panel">
            <p className="text-[11px] text-slate-500 mb-2">
              A parent of {preview.group} is asked {preview.questions.length} question(s) — saved wording, not your unsaved edits
            </p>
            <ol className="space-y-1 text-xs text-slate-200 list-decimal list-inside">
              {preview.questions.map((q) => (
                <li key={q.id}>
                  {q.label}
                  <Badge className="ml-2 text-[10px] bg-slate-800 text-slate-400">{TYPE_LABELS[q.type]}</Badge>
                </li>
              ))}
            </ol>
          </div>
        )}

        <DialogFooter>
          <Button onClick={save} disabled={busy} className="bg-cyan-600 hover:bg-cyan-500"
            data-testid="questions-save">Save questions</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
